import { useState, useEffect } from "react";
import { Shield, AlertTriangle, Lock, Eye, RefreshCw, ExternalLink } from "lucide-react";
import api from "../api";
import type { SafetyLog, SafetyCategories } from "../api";

/* ═══════════════════════════════════════════════════════════════ */
/*  SAFETY PAGE                                                    */
/* ═══════════════════════════════════════════════════════════════ */

export function SafetyPage() {
  const [logs, setLogs] = useState<SafetyLog[]>([]);
  const [categories, setCategories] = useState<SafetyCategories | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"logs" | "categories">("logs");

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [logsData, catsData] = await Promise.all([
        api.safetyLogs(undefined, 100),
        api.safetyCategories(),
      ]);
      setLogs(logsData);
      setCategories(catsData);
    } catch (err) {
      console.error("Failed to load safety data:", err);
    } finally {
      setLoading(false);
    }
  }

  const blockedCount = logs.filter(l => l.blocked).length;
  const gatedCount = logs.filter(l => !l.blocked).length;

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--space-5)" }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
            <Shield size={20} /> Safety Classifier
          </h2>
          <p className="muted" style={{ marginTop: 4, fontSize: 13 }}>
            Pre-pipeline safety checks. Every request is classified before the pipeline runs.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <a
            href="https://ocnexus.dev/safety"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost"
            style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}
          >
            <ExternalLink size={12} /> Safety Policy
          </a>
          <button className="btn btn-ghost" onClick={loadData} style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "var(--space-3)", marginBottom: "var(--space-5)" }}>
        <div className="card" style={{ padding: "var(--space-4)" }}>
          <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>Total Checks</div>
          <div style={{ fontSize: 28, fontWeight: 700, marginTop: 4 }}>{logs.length}</div>
        </div>
        <div className="card" style={{ padding: "var(--space-4)" }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--red)" }}>Blocked</div>
          <div style={{ fontSize: 28, fontWeight: 700, marginTop: 4, color: "var(--red)" }}>{blockedCount}</div>
        </div>
        <div className="card" style={{ padding: "var(--space-4)" }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--yellow)" }}>Gated</div>
          <div style={{ fontSize: 28, fontWeight: 700, marginTop: 4, color: "var(--yellow)" }}>{gatedCount}</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "var(--space-1)", marginBottom: "var(--space-4)", borderBottom: "1px solid var(--border)" }}>
        <button
          className={`tab-btn ${tab === "logs" ? "active" : ""}`}
          onClick={() => setTab("logs")}
          style={{
            padding: "var(--space-2) var(--space-4)",
            fontSize: 13,
            fontWeight: tab === "logs" ? 600 : 400,
            color: tab === "logs" ? "var(--text)" : "var(--text-dim)",
            background: "none",
            border: "none",
            borderBottom: tab === "logs" ? "2px solid var(--accent)" : "2px solid transparent",
            cursor: "pointer",
          }}
        >
          Recent Logs
        </button>
        <button
          className={`tab-btn ${tab === "categories" ? "active" : ""}`}
          onClick={() => setTab("categories")}
          style={{
            padding: "var(--space-2) var(--space-4)",
            fontSize: 13,
            fontWeight: tab === "categories" ? 600 : 400,
            color: tab === "categories" ? "var(--text)" : "var(--text-dim)",
            background: "none",
            border: "none",
            borderBottom: tab === "categories" ? "2px solid var(--accent)" : "2px solid transparent",
            cursor: "pointer",
          }}
        >
          Categories
        </button>
      </div>

      {loading ? (
        <div className="empty-state">
          <div className="spinner" />
          <span className="muted">Loading safety data...</span>
        </div>
      ) : tab === "logs" ? (
        <LogsTab logs={logs} />
      ) : (
        <CategoriesTab categories={categories} />
      )}
    </div>
  );
}

/* ─── Logs Tab ─────────────────────────────────────────────────── */

function LogsTab({ logs }: { logs: SafetyLog[] }) {
  if (logs.length === 0) {
    return (
      <div className="empty-state" style={{ padding: "var(--space-8)" }}>
        <Shield size={32} style={{ opacity: 0.3 }} />
        <p className="muted">No safety events recorded yet.</p>
        <p className="muted" style={{ fontSize: 12 }}>Safety logs will appear here when requests are classified.</p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
      {logs.map(log => (
        <div key={log.id} className="card" style={{ padding: "var(--space-3) var(--space-4)", display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
          {log.blocked ? (
            <AlertTriangle size={16} style={{ color: "var(--red)", flexShrink: 0 }} />
          ) : (
            <Lock size={16} style={{ color: "var(--yellow)", flexShrink: 0 }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
              <span style={{
                fontSize: 11,
                fontWeight: 600,
                padding: "1px 6px",
                borderRadius: 4,
                background: log.blocked ? "rgba(239,68,68,0.15)" : "rgba(234,179,8,0.15)",
                color: log.blocked ? "var(--red)" : "var(--yellow)",
              }}>
                {log.blocked ? "BLOCKED" : "GATED"}
              </span>
              <code style={{ fontSize: 12, color: "var(--text-dim)" }}>{log.category}</code>
              {log.project_id && (
                <span className="muted" style={{ fontSize: 11 }}>· {log.project_id}</span>
              )}
            </div>
            {log.reason && (
              <p className="muted" style={{ fontSize: 12, marginTop: 2 }}>{log.reason}</p>
            )}
          </div>
          <span className="muted" style={{ fontSize: 11, flexShrink: 0 }}>
            {new Date(log.created_at).toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ─── Categories Tab ───────────────────────────────────────────── */

function CategoriesTab({ categories }: { categories: SafetyCategories | null }) {
  if (!categories) {
    return <div className="muted">Failed to load categories.</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
      {/* Prohibited */}
      <div>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: "var(--red)", display: "flex", alignItems: "center", gap: 6, marginBottom: "var(--space-3)" }}>
          <AlertTriangle size={14} /> Prohibited Categories
        </h3>
        <p className="muted" style={{ fontSize: 12, marginBottom: "var(--space-3)" }}>
          Always blocked — no pipeline run, no cost incurred.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          {categories.prohibited.map(cat => (
            <div key={cat.id} className="card" style={{ padding: "var(--space-3) var(--space-4)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginBottom: 4 }}>
                <code style={{ fontSize: 12, fontWeight: 600, color: "var(--red)" }}>{cat.id}</code>
              </div>
              <p className="muted" style={{ fontSize: 12, lineHeight: 1.5 }}>{cat.decline_message}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Restricted */}
      <div>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: "var(--yellow)", display: "flex", alignItems: "center", gap: 6, marginBottom: "var(--space-3)" }}>
          <Lock size={14} /> Restricted Categories
        </h3>
        <p className="muted" style={{ fontSize: 12, marginBottom: "var(--space-3)" }}>
          Require human approval gate before pipeline runs.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          {categories.restricted.map(cat => (
            <div key={cat.id} className="card" style={{ padding: "var(--space-3) var(--space-4)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginBottom: 4 }}>
                <code style={{ fontSize: 12, fontWeight: 600, color: "var(--yellow)" }}>{cat.id}</code>
              </div>
              <p className="muted" style={{ fontSize: 12, lineHeight: 1.5 }}>{cat.gate_message}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
